import { getOrgSettings, manageOrg, type OrgSettings } from "../API";

/**
 * Everything the Settings tabs can ask of the server. The page container
 * passes one in; the default talks to `/get_org_settings` and `/manage_org`,
 * and a test or the onboarding tour can pass a scripted one.
 */
export interface OrgActions {
  load(): Promise<OrgSettings>;
  setTimezone(timezone: string): Promise<unknown>;
  /** Exactly these emails hold a seat afterwards. Rejects with
   *  `SeatIncreaseRequired` when that grows past the paid cap, until re-sent
   *  with `approveIncrease`. */
  setSeats(emails: string[], approveIncrease?: boolean): Promise<unknown>;
  addUser(email: string, firstName: string): Promise<unknown>;
  setAdmin(email: string, isAdmin: boolean): Promise<unknown>;
  purchaseSeats(count: number): Promise<unknown>;
  reduceSeats(count: number): Promise<unknown>;
  billingPortal(): Promise<{ portal_url?: string }>;
}

export const apiOrgActions: OrgActions = {
  load: () => getOrgSettings(),
  setTimezone: (timezone) => manageOrg({ action: "set_timezone", timezone }),
  setSeats: (emails, approve_increase) =>
    manageOrg({ action: "set_seats", emails, approve_increase }),
  addUser: (email, first_name) => manageOrg({ action: "add_user", email, first_name }),
  setAdmin: (email, is_admin) => manageOrg({ action: "set_user_admin", email, is_admin }),
  purchaseSeats: (count) => manageOrg({ action: "purchase_seats", count }),
  reduceSeats: (count) => manageOrg({ action: "reduce_seats", count }),
  billingPortal: () => manageOrg({ action: "billing_portal" }),
};

/** What every Settings tab receives from the container. */
export type SettingsPageProps = {
  data: OrgSettings;
  busy: boolean;
  /** Runs a mutation, then reloads; a rejection shows as the page's error. */
  run: (fn: () => Promise<unknown>, fallback?: string) => Promise<void>;
  actions: OrgActions;
};
